/**
 * Translation/Term Override System
 *
 * This system currently provides term customization within English, NOT full
 * internationalization (i18n). It allows units to override specific terms
 * (e.g., "Group" → "Team", "Student" → "Participant") while keeping the
 * surrounding English text unchanged.
 *
 * We intentionally use simple string interpolation like `Create ${groupTerm}`
 * rather than making entire phrases translation keys. Full i18n would require
 * phrase-level keys with placeholders to handle different grammatical structures
 * across languages, but that's out of scope for the current English-only term
 * customization use case.
 *
 * If multi-language support becomes a requirement, phrases containing
 * translated terms would need to become full translation keys.
 */
import { observable } from "mobx";
import enUS from "./lang/en-us.json";
export function isTranslationKey(key) {
    return key in enUS;
}
// Module-level MobX observable state for term overrides.
const moduleTermOverrides = observable.box(undefined);
/**
 * Set module-level term overrides.
 * Call this when the unit configuration loads.
 *
 * After calling this, translate() will use these values by default,
 * eliminating the need to pass options through each call.
 */
export function setTermOverrides(termOverrides) {
    moduleTermOverrides.set(termOverrides);
}
/**
 * Clear module-level overrides (useful for testing).
 */
export function clearTermOverrides() {
    moduleTermOverrides.set(undefined);
}
export function getTermOverride(key) {
    const overrides = moduleTermOverrides.get();
    return overrides === null || overrides === void 0 ? void 0 : overrides[key];
}
/**
 * Get the default/base value for a key, ignoring any overrides.
 */
export function getDefaultValue(key) {
    var _a;
    return (_a = enUS[key]) !== null && _a !== void 0 ? _a : key;
}
/**
 * Translate a key to its display string.
 *
 * Resolution order:
 * 1. Module-level termOverrides (set via setTermOverrides())
 * 2. Default value from en-us.json
 * 3. The key itself as fallback
 *
 */
export function translate(key) {
    return getTermOverride(key) || getDefaultValue(key);
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidHJhbnNsYXRlLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vc3JjL3V0aWxpdGllcy90cmFuc2xhdGlvbi90cmFuc2xhdGUudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUE7Ozs7Ozs7Ozs7Ozs7Ozs7R0FnQkc7QUFFSCxPQUFPLEVBQUUsVUFBVSxFQUFFLE1BQU0sTUFBTSxDQUFDO0FBQ2xDLE9BQU8sSUFBSSxNQUFNLG1CQUFtQixDQUFDO0FBR3JDLE1BQU0sVUFBVSxnQkFBZ0IsQ0FBQyxHQUFXO0lBQzFDLE9BQU8sR0FBRyxJQUFJLElBQUksQ0FBQztBQUNyQixDQUFDO0FBRUQseURBQXlEO0FBQ3pELE1BQU0sbUJBQW1CLEdBQUcsVUFBVSxDQUFDLEdBQUcsQ0FBcUMsU0FBUyxDQUFDLENBQUM7QUFFMUY7Ozs7OztHQU1HO0FBQ0gsTUFBTSxVQUFVLGdCQUFnQixDQUFDLGFBQXNDO0lBQ3JFLG1CQUFtQixDQUFDLEdBQUcsQ0FBQyxhQUFhLENBQUMsQ0FBQztBQUN6QyxDQUFDO0FBRUQ7O0dBRUc7QUFDSCxNQUFNLFVBQVUsa0JBQWtCO0lBQ2hDLG1CQUFtQixDQUFDLEdBQUcsQ0FBQyxTQUFTLENBQUMsQ0FBQztBQUNyQyxDQUFDO0FBRUQsTUFBTSxVQUFVLGVBQWUsQ0FBQyxHQUF1QjtJQUNyRCxNQUFNLFNBQVMsR0FBRyxtQkFBbUIsQ0FBQyxHQUFHLEVBQUUsQ0FBQztJQUM1QyxPQUFPLFNBQVMsYUFBVCxTQUFTLHVCQUFULFNBQVMsQ0FBRyxHQUFHLENBQUMsQ0FBQztBQUMxQixDQUFDO0FBRUQ7O0dBRUc7QUFDSCxNQUFNLFVBQVUsZUFBZSxDQUFDLEdBQXVCOztJQUNyRCxPQUFPLE1BQUEsSUFBSSxDQUFDLEdBQUcsQ0FBQyxtQ0FBSSxHQUFHLENBQUM7QUFDMUIsQ0FBQztBQUVEOzs7Ozs7OztHQVFHO0FBQ0gsTUFBTSxVQUFVLFNBQVMsQ0FBQyxHQUF1QjtJQUMvQyxPQUFPLGVBQWUsQ0FBQyxHQUFHLENBQUMsSUFBSSxlQUFlLENBQUMsR0FBRyxDQUFDLENBQUM7QUFDdEQsQ0FBQyJ9